import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const alertVariants = cva(
  'relative w-full [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4',
  {
    variants: {
      variant: {
        default: '',
        destructive: '',
        success: '',
        warning: '',
        info: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        alertVariants({ variant }),
        'rounded-[var(--radius-md)] p-[var(--spacing-md)] border',
        // Default: background --card-bg, border --border, text --text-primary
        variant === 'default' && 'bg-[var(--card-bg)] border-[var(--border)] text-[var(--text-primary)]',
        // Destructive: use --alert-destructive-bg, border --error, text --error
        variant === 'destructive' && 'bg-[var(--alert-destructive-bg)] border-[var(--error)] text-[var(--error)]',
        // Success: use --alert-success-bg, border --success, text --success
        variant === 'success' && 'bg-[var(--alert-success-bg)] border-[var(--success)] text-[var(--success)]',
        // Warning: use --alert-warning-bg, border --warning, text --warning
        variant === 'warning' && 'bg-[var(--alert-warning-bg)] border-[var(--warning)] text-[var(--warning)]',
        // Info: use --alert-info-bg, border --info, text --info
        variant === 'info' && 'bg-[var(--alert-info-bg)] border-[var(--info)] text-[var(--info)]',
        className
      )}
      style={style}
      {...props}
    />
  )
})
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, style, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('leading-none tracking-tight', className)}
    style={{
      fontWeight: 'var(--font-semibold)',
      marginBottom: 'var(--spacing-xs)',
      ...style,
    }}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('[&_p]:leading-relaxed', className)}
    style={{
      fontSize: 'var(--text-sm)',
      opacity: 0.9,
      ...style,
    }}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertDescription, AlertTitle }

